import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import {
  Camera, Upload, FileText, Loader2, Check, X, Edit2,
  Receipt, ChevronDown, ChevronUp, Fuel, AlertCircle,
  Trash2, Eye, RotateCcw, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Carburant', 'Reparations', 'Entretien preventif', 'Peages', 'Assurances',
  'Permis et licences', 'Pieces et fournitures', 'Location equipement',
  'Sous-traitance', 'Loyer / Hypotheque', 'Telecommunications',
  'Fournitures de bureau', 'Divers'
];

const PAYMENT_METHODS = [
  { value: 'carte_credit', label: 'Carte de credit' },
  { value: 'carte_debit', label: 'Carte de debit' },
  { value: 'comptant', label: 'Comptant' },
  { value: 'virement', label: 'Virement' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'compte_client', label: 'Compte client' },
];

const STATUS_LABELS = {
  uploaded: 'Televerse',
  extracting: 'Analyse en cours...',
  extracted: 'Donnees extraites',
  validated: 'Validee',
  error: 'Erreur',
};

const STATUS_COLORS = {
  uploaded: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  extracting: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  extracted: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  validated: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  error: 'bg-red-500/15 text-red-400 border-red-500/30',
};

// ==================== UPLOAD ZONE ====================

const UploadZone = ({ token, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'];
    if (!allowed.includes(file.type) && !file.name.match(/\.(pdf|jpg|jpeg|png|webp|heic|heif)$/i)) {
      toast.error('Format non supporte. Utilisez PDF, JPEG, PNG ou WebP.');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 20 Mo)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API_URL}/api/invoices/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Fichier televerse avec succes');
      onUploaded(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de telechargement');
    } finally {
      setUploading(false);
    }
  }, [token, onUploaded]);

  return (
    <div className="space-y-4">
      <div
        className="border-2 border-dashed border-slate-600 rounded-2xl p-8 text-center hover:border-amber-500/50 transition-colors cursor-pointer"
        onClick={() => fileRef.current?.click()}
        data-testid="upload-zone"
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
            <p className="text-sm text-slate-400">Telechargement en cours...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Upload className="w-10 h-10 text-slate-500" />
            <p className="text-sm text-slate-300 font-medium">Glissez un fichier ou cliquez pour parcourir</p>
            <p className="text-xs text-slate-500">PDF, JPEG, PNG, WebP - Max 20 Mo</p>
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*,application/pdf"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="file-input"
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="camera-input"
      />

      <div className="flex gap-3">
        <button
          onClick={() => cameraRef.current?.click()}
          disabled={uploading}
          className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold py-3 px-4 rounded-xl transition-colors disabled:opacity-50"
          data-testid="camera-btn"
        >
          <Camera size={20} />
          <span>Prendre une photo</span>
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex-1 flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 px-4 rounded-xl transition-colors disabled:opacity-50"
          data-testid="browse-btn"
        >
          <FileText size={20} />
          <span>Parcourir</span>
        </button>
      </div>
      <div className="bg-slate-800/50 rounded-lg px-3 py-2 text-[11px] text-slate-500 space-y-0.5">
        <p>Conseils pour un meilleur scan :</p>
        <p>- Posez le document bien a plat sur une surface</p>
        <p>- Bonne lumiere, evitez les ombres</p>
        <p>- Cadrez droit, tout le document visible</p>
      </div>
    </div>
  );
};

// ==================== EXTRACTION REVIEW ====================

const ExtractionReview = ({ invoice, token, onValidated, onCancel, onRetry }) => {
  const data = invoice.validated_data || invoice.extracted_data || {};
  const isEmpty = !data.supplier_name && !data.total && !data.subtotal;
  const [form, setForm] = useState({
    supplier_name: data.supplier_name || '',
    invoice_number: data.invoice_number || '',
    invoice_date: data.invoice_date || new Date().toISOString().split('T')[0],
    subtotal: data.subtotal || 0,
    tax_gst: data.tax_gst || 0,
    tax_qst: data.tax_qst || 0,
    total: data.total || 0,
    category_suggestion: data.category_suggestion || 'Divers',
    payment_method: data.payment_method || '',
    is_fuel: data.is_fuel || false,
    fuel_data: data.fuel_data || { litres: null, price_per_litre: null, fuel_type: null, province_state: 'QC', odometer_reading: null },
    notes: data.notes || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [showFuel, setShowFuel] = useState(form.is_fuel);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    const loadPreview = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/invoices/file/${invoice.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        setPreviewUrl(URL.createObjectURL(res.data));
      } catch { /* silent */ }
    };
    loadPreview();
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice.id, token]);

  const updateField = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (['subtotal', 'tax_gst', 'tax_qst'].includes(field)) {
        const sub = field === 'subtotal' ? parseFloat(value) || 0 : prev.subtotal;
        const gst = field === 'tax_gst' ? parseFloat(value) || 0 : prev.tax_gst;
        const qst = field === 'tax_qst' ? parseFloat(value) || 0 : prev.tax_qst;
        next.total = Math.round((sub + gst + qst) * 100) / 100;
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!form.supplier_name.trim()) {
      toast.error('Le nom du fournisseur est requis');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/invoices/${invoice.id}/validate`,
        { validated_data: form, create_expense: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Facture validee et depense creee');
      onValidated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de validation');
    } finally {
      setSubmitting(false);
    }
  };

  const confidence = data.confidence;
  const inputCls = "w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent";
  const labelCls = "block text-xs font-medium text-slate-400 mb-1";

  return (
    <div className="space-y-4" data-testid="extraction-review">
      {confidence != null && (
        <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${confidence >= 0.8 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
          <AlertCircle size={14} />
          <span>Confiance IA: {Math.round(confidence * 100)}% {confidence < 0.8 ? '- Verifiez bien les donnees' : ''}</span>
        </div>
      )}

      {isEmpty && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
            <AlertCircle size={16} />
            <span>L'IA n'a pas pu lire la facture</span>
          </div>
          <p className="text-xs text-slate-400">
            Pour de meilleurs resultats : posez le document a plat, bonne lumiere, prenez la photo bien droite au-dessus.
          </p>
          {onRetry && (
            <button onClick={onRetry}
              className="w-full flex items-center justify-center gap-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 py-2 rounded-lg text-sm font-medium transition-colors"
              data-testid="retry-scan-btn">
              <RotateCcw size={14} />
              Reprendre une photo
            </button>
          )}
        </div>
      )}

      {previewUrl && (
        <div className="bg-slate-800 rounded-xl p-2 max-h-48 overflow-hidden">
          {invoice.content_type === 'application/pdf' ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm p-4">
              <FileText size={20} />
              <span>{invoice.original_filename}</span>
            </div>
          ) : (
            <img src={previewUrl} alt="Facture" className="w-full h-44 object-contain rounded-lg" />
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className={labelCls}>Fournisseur *</label>
          <input className={inputCls} value={form.supplier_name} onChange={e => updateField('supplier_name', e.target.value)} data-testid="field-supplier" />
        </div>
        <div>
          <label className={labelCls}>No. Facture</label>
          <input className={inputCls} value={form.invoice_number || ''} onChange={e => updateField('invoice_number', e.target.value)} data-testid="field-invoice-number" />
        </div>
        <div>
          <label className={labelCls}>Date</label>
          <input type="date" className={inputCls} value={form.invoice_date} onChange={e => updateField('invoice_date', e.target.value)} data-testid="field-date" />
        </div>
        <div>
          <label className={labelCls}>Sous-total ($)</label>
          <input type="number" step="0.01" className={inputCls} value={form.subtotal} onChange={e => updateField('subtotal', e.target.value)} data-testid="field-subtotal" />
        </div>
        <div>
          <label className={labelCls}>TPS ($)</label>
          <input type="number" step="0.01" className={inputCls} value={form.tax_gst} onChange={e => updateField('tax_gst', e.target.value)} data-testid="field-tps" />
        </div>
        <div>
          <label className={labelCls}>TVQ ($)</label>
          <input type="number" step="0.01" className={inputCls} value={form.tax_qst} onChange={e => updateField('tax_qst', e.target.value)} data-testid="field-tvq" />
        </div>
        <div>
          <label className={labelCls}>Total ($)</label>
          <input type="number" step="0.01" className={`${inputCls} font-bold text-amber-400`} value={form.total} onChange={e => updateField('total', e.target.value)} data-testid="field-total" />
        </div>
        <div>
          <label className={labelCls}>Categorie</label>
          <select className={inputCls} value={form.category_suggestion} onChange={e => updateField('category_suggestion', e.target.value)} data-testid="field-category">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Methode de paiement</label>
          <select className={inputCls} value={form.payment_method || ''} onChange={e => updateField('payment_method', e.target.value)} data-testid="field-payment">
            <option value="">--</option>
            {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
      </div>

      <button
        onClick={() => { setShowFuel(!showFuel); updateField('is_fuel', !showFuel); }}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${showFuel ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}
        data-testid="fuel-toggle"
      >
        <span className="flex items-center gap-2"><Fuel size={16} /> Donnees carburant (IFTA)</span>
        {showFuel ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {showFuel && (
        <div className="grid grid-cols-2 gap-3 bg-slate-800/50 p-3 rounded-xl border border-slate-700">
          <div>
            <label className={labelCls}>Litres</label>
            <input type="number" step="0.01" className={inputCls} value={form.fuel_data?.litres || ''} onChange={e => updateField('fuel_data', { ...form.fuel_data, litres: parseFloat(e.target.value) || null })} data-testid="field-litres" />
          </div>
          <div>
            <label className={labelCls}>Prix/L ($)</label>
            <input type="number" step="0.001" className={inputCls} value={form.fuel_data?.price_per_litre || ''} onChange={e => updateField('fuel_data', { ...form.fuel_data, price_per_litre: parseFloat(e.target.value) || null })} data-testid="field-price-litre" />
          </div>
          <div>
            <label className={labelCls}>Province/Etat</label>
            <input className={inputCls} value={form.fuel_data?.province_state || ''} onChange={e => updateField('fuel_data', { ...form.fuel_data, province_state: e.target.value })} placeholder="QC, ON, NY..." data-testid="field-province" />
          </div>
          <div>
            <label className={labelCls}>Odometre (km)</label>
            <input type="number" className={inputCls} value={form.fuel_data?.odometer_reading || ''} onChange={e => updateField('fuel_data', { ...form.fuel_data, odometer_reading: parseInt(e.target.value) || null })} data-testid="field-odometer" />
          </div>
        </div>
      )}

      <div>
        <label className={labelCls}>Notes</label>
        <textarea className={`${inputCls} resize-none`} rows={2} value={form.notes} onChange={e => updateField('notes', e.target.value)} data-testid="field-notes" />
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={onCancel} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl font-medium transition-colors" data-testid="cancel-btn">
          Annuler
        </button>
        <button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-amber-500 hover:bg-amber-400 text-black py-3 rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="validate-btn">
          {submitting ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
          Valider et soumettre
        </button>
      </div>
    </div>
  );
};

// ==================== INVOICE LIST ====================

const InvoiceList = ({ invoices, onSelect }) => {
  if (!invoices.length) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Receipt className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p className="text-sm">Aucune facture pour le moment</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="invoice-list">
      {invoices.map(inv => (
        <button
          key={inv.id}
          onClick={() => onSelect(inv)}
          className="w-full text-left bg-slate-800/60 hover:bg-slate-800 border border-slate-700/50 rounded-xl p-3 transition-colors"
          data-testid={`invoice-${inv.id}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`p-2 rounded-lg ${inv.content_type === 'application/pdf' ? 'bg-red-500/10' : 'bg-blue-500/10'}`}>
                {inv.content_type === 'application/pdf' ? <FileText size={18} className="text-red-400" /> : <ImageIcon size={18} className="text-blue-400" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm text-white font-medium truncate">
                  {inv.validated_data?.supplier_name || inv.extracted_data?.supplier_name || inv.original_filename}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {inv.validated_data?.invoice_date || inv.extracted_data?.invoice_date || inv.created_at?.split('T')[0]}
                  {(inv.validated_data?.total || inv.extracted_data?.total) && (
                    <span className="ml-2 text-amber-400 font-medium">
                      {(inv.validated_data?.total || inv.extracted_data?.total)?.toFixed(2)} $
                    </span>
                  )}
                </p>
              </div>
            </div>
            <span className={`text-[10px] px-2 py-1 rounded-full border whitespace-nowrap ${STATUS_COLORS[inv.status] || STATUS_COLORS.uploaded}`}>
              {STATUS_LABELS[inv.status] || inv.status}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const InvoiceScanner = ({ token, title = "Depenses" }) => {
  const [tab, setTab] = useState('scan');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [extracting, setExtracting] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(res.data.invoices || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleUploaded = async (invoice) => {
    setCurrentInvoice(invoice);
    setExtracting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/invoices/${invoice.id}/extract`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentInvoice(res.data);
      if (res.data.status === 'error') {
        toast.error("L'extraction a echoue. Vous pouvez remplir manuellement.");
      }
    } catch (err) {
      toast.error("Erreur lors de l'analyse. Vous pouvez remplir manuellement.");
      setCurrentInvoice({ ...invoice, status: 'extracted', extracted_data: {} });
    } finally {
      setExtracting(false);
    }
  };

  const handleValidated = () => {
    setCurrentInvoice(null);
    setTab('history');
    fetchInvoices();
  };

  const tabs = [
    { id: 'scan', label: 'Scanner', icon: Camera },
    { id: 'history', label: 'Historique', icon: Receipt },
  ];

  return (
    <div className="min-h-screen bg-slate-950 pb-24" data-testid="invoice-scanner">
      <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800 px-4 pt-6 pb-4">
        <h1 className="text-xl font-bold text-white mb-4">{title}</h1>
        <div className="flex gap-1 bg-slate-800/50 rounded-xl p-1">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setCurrentInvoice(null); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? 'bg-amber-500 text-black' : 'text-slate-400 hover:text-white'}`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon size={16} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-4">
        {tab === 'scan' && (
          <>
            {!currentInvoice && !extracting && (
              <UploadZone token={token} onUploaded={handleUploaded} />
            )}

            {extracting && (
              <div className="flex flex-col items-center gap-4 py-16" data-testid="extracting-indicator">
                <div className="relative">
                  <Loader2 className="w-14 h-14 text-amber-500 animate-spin" />
                  <Receipt className="w-6 h-6 text-amber-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                </div>
                <div className="text-center">
                  <p className="text-white font-medium">Analyse de la facture...</p>
                  <p className="text-xs text-slate-500 mt-1">GPT-4o extrait les donnees automatiquement</p>
                </div>
              </div>
            )}

            {currentInvoice && !extracting && (
              <ExtractionReview
                invoice={currentInvoice}
                token={token}
                onValidated={handleValidated}
                onCancel={() => setCurrentInvoice(null)}
                onRetry={() => setCurrentInvoice(null)}
              />
            )}
          </>
        )}

        {tab === 'history' && (
          loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
            </div>
          ) : (
            <InvoiceList
              invoices={invoices}
              onSelect={(inv) => {
                if (inv.status === 'extracted' || inv.status === 'uploaded') {
                  setCurrentInvoice(inv);
                  setTab('scan');
                }
              }}
            />
          )
        )}
      </div>
    </div>
  );
};

export default InvoiceScanner;
